/* eslint-disable import/no-default-export,jsx-a11y/label-has-associated-control,jsx-a11y/no-onchange,react/no-danger */
import classNames from 'classnames';
import {
  ButtonSizes,
  PrimaryButton,
} from 'app/common/components/controls/button/button';
import { TeamType } from 'app/gamma/src/modules/state/models/teams';
import React from 'react';
import { EnterpriseModel } from 'app/gamma/src/types/models';
import preventDefault from 'app/gamma/src/util/prevent-default';
import { byAttributeCaseInsensitive } from 'app/gamma/src/util/sort';
import styles from './create-team.less';

import { TeamTestIds } from '@trello/webdriverio-tests';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { dontUpsell } from '@trello/config';
import { TeamTypeSelect } from 'app/src/components/TeamTypeSelect';
import { featureFlagClient } from '@trello/feature-flag-client';

const formatBody = forTemplate('org_create');
const formatProduct = forNamespace('products');

const idPrefix = `${Date.now()}-`; // for id specificity

interface CreateTeamProps {
  teamType: TeamType;
  enterprises?: EnterpriseModel[];
  submitCreateTeam: (action: {
    type: TeamType;
    displayName: string;
    teamType?: string;
    desc?: string;
    enterprise?: string;
    redirectAfterCreate?: boolean;
  }) => void;
}

interface CreateTeamState {
  displayName: string;
  desc?: string;
  enterprise?: string;
  isSubmitting: boolean;
  teamType?: string;
}

class CreateTeam extends React.Component<CreateTeamProps, CreateTeamState> {
  state = {
    displayName: '',
    teamType: undefined,
    desc: undefined,
    enterprise:
      this.props.teamType === TeamType.Enterprise &&
      this.props.enterprises &&
      this.props.enterprises.length
        ? this.props.enterprises[0].id
        : undefined,
    isSubmitting: false,
  };

  componentDidMount() {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.CREATE_TEAM_MENU,
      context: {
        teamType: this.props.teamType,
      },
    });
  }

  onChangeTeamName = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      displayName: e.target.value,
    });
  };

  onChangeTeamType = (teamTypeValue: string) => {
    this.setState({
      teamType: teamTypeValue,
    });
  };

  onChangeEnterprise: React.FormEventHandler<HTMLSelectElement> = (e) => {
    this.setState({
      enterprise: e.currentTarget.value,
    });
  };

  onChangeTeamDescription: React.FormEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    this.setState({
      desc: e.currentTarget.value,
    });
  };

  onCreateTeam: React.FormEventHandler<HTMLFormElement> = (e) => {
    this.setState({ isSubmitting: true });
    const { teamType } = this.props;
    const { displayName, desc, enterprise } = this.state;
    const teamVerticalType = this.state.teamType;
    const redirectAfterCreate = !!enterprise;
    this.props.submitCreateTeam({
      type: teamType,
      displayName: displayName.trim(),
      teamType: teamVerticalType,
      desc,
      enterprise,
      redirectAfterCreate,
    });
  };

  onBusinessLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // TODO: track this link click
  }

  getEnterprises = () => {
    const { enterprises } = this.props;
    if (!enterprises || !enterprises.length) {
      return undefined;
    }

    return enterprises.length === 1 ? (
      <p id={`${idPrefix}create-team-enterprise-name`}>
        {enterprises[0].displayName}
      </p>
    ) : (
      <select
        className={styles.select}
        id={`${idPrefix}create-team-enterprise-name`}
        onChange={this.onChangeEnterprise}
        disabled={this.state.isSubmitting}
      >
        {enterprises
          .sort(byAttributeCaseInsensitive('displayName'))
          .map((ent, index) => {
            const { id, displayName } = ent;

            return (
              <option aria-selected={index === 0} value={id} key={id}>
                {displayName}
              </option>
            );
          })}
      </select>
    );
  };

  render() {
    const { displayName, isSubmitting } = this.state;
    const { teamType } = this.props;
    const isTeamTypesFieldEnabled = featureFlagClient.get(
      'btg.team-vertical-selection',
      false,
    );
    const teamVerticalType = this.state.teamType;
    const isSubmitDisabled =
      !displayName.trim() ||
      (isTeamTypesFieldEnabled && !teamVerticalType) ||
      isSubmitting;

    return (
      <form onSubmit={preventDefault(this.onCreateTeam)}>
        <label htmlFor={`${idPrefix}create-team-org-display-name`}>
          {formatBody('name')}
        </label>
        <input
          autoFocus={true}
          className={styles.textInput}
          dir="auto"
          id={`${idPrefix}create-team-org-display-name`}
          maxLength={100}
          onChange={this.onChangeTeamName}
          value={this.state.displayName}
          tabIndex={0}
          type="text"
          data-test-id={TeamTestIds.CreateTeamNameInput}
          disabled={isSubmitting}
        />
        {isTeamTypesFieldEnabled && (
          <TeamTypeSelect
            onChange={this.onChangeTeamType}
            isDisabled={isSubmitting}
          />
        )}
        {teamType === TeamType.Enterprise ? (
          <>
            <label htmlFor={`${idPrefix}create-team-enterprise-name`}>
              {formatProduct('enterprise')}
            </label>
            {this.getEnterprises()}
          </>
        ) : (
          <>
            <label
              htmlFor={`${idPrefix}create-team-org-description`}
              dangerouslySetInnerHTML={{
                __html: formatBody('description-optional'),
              }}
            />
            <textarea
              className={styles.textAreaInput}
              dir="auto"
              id={`${idPrefix}create-team-org-description`}
              name="desc"
              onChange={this.onChangeTeamDescription}
              tabIndex={0}
              disabled={isSubmitting}
            />
          </>
        )}
        <PrimaryButton
          className={styles.submitButton}
          type="submit"
          size={ButtonSizes.WIDE}
          disabled={isSubmitDisabled}
          testId={TeamTestIds.CreateTeamSubmitButton}
        >
          {formatBody('create')}
        </PrimaryButton>
        <hr className={styles.separator} />
        <p className={classNames(styles.quiet, styles.bottomParagraph)}>
          {formatBody(
            teamType === TeamType.Enterprise
              ? 'a-team-is-a-group-of-boards-and-people-owned'
              : 'a-team-is-a-group-of-boards-and-people',
          )}
        </p>
        {!dontUpsell && teamType !== TeamType.Enterprise && (
          <p className={classNames(styles.quiet, styles.bottomParagraph)}>
            <span
              dangerouslySetInnerHTML={{
                __html: `${formatBody(
                  'business-class-gives-your-organization-more',
                )} `, // intentional space appended
              }}
            />
            <a
              className={styles.quiet}
              onClick={this.onBusinessLinkClick}
              href="/business-class"
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatBody('learn-more')}
            </a>
          </p>
        )}
      </form>
    );
  }
}

export default CreateTeam;
